import { useRef, useCallback } from 'react'
import { supabase } from '../services/supabaseClient'
import { useAppSelector } from '../store/hooks'

const DEBOUNCE_MS = 800
const LS_PREFIX = 'gh_'

/**
 * Hook that replicates the original app's auto-save behaviour:
 * - Debounces saves by 800ms
 * - Saves to both localStorage (backup) and Supabase (cloud)
 * - Loads cloud-first with localStorage fallback
 */
export function useAutoSave(toolId: string) {
  const userId = useAppSelector((s) => s.auth.user?.id)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveToLocal = useCallback(
    (data: Record<string, unknown>) => {
      try {
        localStorage.setItem(`${LS_PREFIX}${toolId}`, JSON.stringify(data))
      } catch {
        // localStorage full or unavailable
      }
    },
    [toolId],
  )

  const loadFromLocal = useCallback((): Record<string, unknown> | null => {
    try {
      const raw = localStorage.getItem(`${LS_PREFIX}${toolId}`)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [toolId])

  const saveToCloud = useCallback(
    async (data: Record<string, unknown>) => {
      if (!userId) return

      // Check if record exists
      const { data: existing } = await supabase
        .from('responses')
        .select('id')
        .eq('user_id', userId)
        .eq('tool_id', toolId)
        .maybeSingle()

      if (existing) {
        await supabase
          .from('responses')
          .update({ data })
          .eq('user_id', userId)
          .eq('tool_id', toolId)
      } else {
        await supabase
          .from('responses')
          .insert({ user_id: userId, tool_id: toolId, data })
      }
    },
    [userId, toolId],
  )

  const loadFromCloud = useCallback(async (): Promise<Record<string, unknown> | null> => {
    if (!userId) return null

    const { data } = await supabase
      .from('responses')
      .select('data')
      .eq('user_id', userId)
      .eq('tool_id', toolId)
      .maybeSingle()

    return (data?.data as Record<string, unknown>) ?? null
  }, [userId, toolId])

  /**
   * Load tool data: cloud-first, localStorage fallback.
   */
  const load = useCallback(async (): Promise<Record<string, unknown>> => {
    const cloud = await loadFromCloud()
    if (cloud) {
      saveToLocal(cloud) // sync local cache
      return cloud
    }
    return loadFromLocal() ?? {}
  }, [loadFromCloud, loadFromLocal, saveToLocal])

  /**
   * Debounced save: writes to localStorage immediately, cloud after debounce.
   */
  const save = useCallback(
    (data: Record<string, unknown>) => {
      saveToLocal(data)

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        saveToCloud(data)
      }, DEBOUNCE_MS)
    },
    [saveToLocal, saveToCloud],
  )

  return { load, save }
}
